/**
 * RouteTransitionOverlay.jsx — Fix #10a (2026-07-07 pass, v3).
 *
 * Full-viewport spinner overlay that mounts ONLY for slow route
 * transitions. The 2 px TopProgressBar is a peripheral hint; this
 * overlay is the unmissable center-of-viewport signal for the user
 * that something is *actually* taking time.
 *
 * v3 revision (2026-07-08 perf pass) — deploy-perf-fix-1:
 *   The v2 revision forced the overlay to stay visible for
 *   MIN_VISIBLE_MS = 480 ms on *every* route change, even when the
 *   commit itself completed in <50 ms (bundle already loaded).
 *   Playwright timings against live production showed a rock-steady
 *   540-560 ms per transition regardless of destination — 480 ms of
 *   which was pure overlay hold-time, felt as a "white flash between
 *   pages". Users had no data to wait on: the bundle was already
 *   parsed, and the destination component mounted in a single frame.
 *
 *   Fix: SHOW-DELAY, don't fixed-hold. Only paint the overlay if the
 *   route commit hasn't produced a fresh DOM within SHOW_DELAY_MS.
 *   If the commit is fast (99 % of internal navigations), the overlay
 *   never appears — no flash, no artificial wait.
 *   If the commit is slow (data-hungry admin pages, first visit to
 *   a lazy chunk), the overlay pops after the perceptual "did anything
 *   happen?" threshold and stays until commit resolves + a short
 *   MIN_VISIBLE_MS grace period so it isn't a jitter.
 *
 * Rules:
 *  - Fires on `useLocation().pathname` change.
 *  - Overlay only paints if the transition takes > SHOW_DELAY_MS.
 *  - Once shown, stays visible for at least MIN_VISIBLE_MS (jitter guard).
 *  - Backdrop: white 82 % + backdrop-blur 3 px, `z-index: 9998`
 *    (below the TopProgressBar's 9999 so both can co-exist).
 *  - Doesn't cover the nav bar — nav has `z-index: 50` per its
 *    fixed positioning, so we sit above the content, below the nav.
 *  - Suppressed on the very first mount (cold page load has its own
 *    HTML shell).
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

// Delay before the overlay is allowed to paint. Below this the user
// can't yet perceive "nothing happened" — no signal is better than a
// flash for instant commits.
const SHOW_DELAY_MS = 220;
// Once painted, hold the overlay at least this long so a late-arriving
// commit doesn't blink the spinner in and out.
const MIN_VISIBLE_MS = 200;

export default function RouteTransitionOverlay() {
    const location = useLocation();
    const [visible, setVisible] = useState(false);
    const firstMountRef = useRef(true);
    const showTimerRef = useRef(null);
    const shownAtRef = useRef(0);
    const hideTimerRef = useRef(null);

    useEffect(() => {
        // Skip cold-load.
        if (firstMountRef.current) {
            firstMountRef.current = false;
            return;
        }

        // Cancel any pending show/hide from a previous transition.
        if (showTimerRef.current) clearTimeout(showTimerRef.current);
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);

        // Arm a show-timer: overlay appears ONLY if the browser hasn't
        // committed the next route within SHOW_DELAY_MS.
        showTimerRef.current = setTimeout(() => {
            setVisible(true);
            shownAtRef.current = performance.now();
        }, SHOW_DELAY_MS);

        // On next paint (route committed), tear down the show-timer
        // and either drop the overlay or schedule its jitter-guarded
        // dismissal.
        const raf = requestAnimationFrame(() => {
            // Route commit likely landed within the same paint; give
            // the DOM one more frame to settle before deciding.
            const nested = requestAnimationFrame(() => {
                if (showTimerRef.current) {
                    clearTimeout(showTimerRef.current);
                    showTimerRef.current = null;
                }
                if (visible) {
                    const elapsed = performance.now() - shownAtRef.current;
                    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
                    hideTimerRef.current = setTimeout(() => setVisible(false), remaining);
                }
            });
            hideTimerRef.current = { cancel: () => cancelAnimationFrame(nested) };
        });

        return () => {
            if (showTimerRef.current) clearTimeout(showTimerRef.current);
            if (hideTimerRef.current) {
                if (typeof hideTimerRef.current.cancel === "function") {
                    hideTimerRef.current.cancel();
                } else {
                    clearTimeout(hideTimerRef.current);
                }
            }
            cancelAnimationFrame(raf);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Issue #68 — guaranteed dismissal backstop. The `if (visible)` check
    // inside the double-rAF above reads a closure snapshot fixed at the
    // moment THIS effect run started (pathname change) — it fires within
    // ~2 frames of that, almost always well before the 220ms show-timer
    // could ever flip `visible` true, so it evaluates `visible` as false
    // for the exact slow-transition case this component exists to handle.
    // Nothing else in the pathname-keyed effect ever re-checks that state
    // afterward, so without this, the overlay only ever got cleared as an
    // accidental side effect of a LATER, unrelated navigation. This effect
    // re-runs precisely when `visible` actually changes, so it always
    // reads the current value — no navigation required to unstick it.
    useEffect(() => {
        if (!visible) return undefined;
        const t = setTimeout(() => setVisible(false), MIN_VISIBLE_MS);
        return () => clearTimeout(t);
    }, [visible]);

    if (!visible) return null;

    return (
        <div
            role="status"
            aria-live="polite"
            aria-label="Loading page"
            data-testid="route-transition-overlay"
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 9998,
                background: "rgba(255, 255, 255, 0.82)",
                backdropFilter: "blur(3px)",
                WebkitBackdropFilter: "blur(3px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                animation: "coda-overlay-in 140ms ease-out forwards",
            }}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 16,
                }}
            >
                <Loader2
                    size={48}
                    strokeWidth={1.6}
                    color="#1A237E"
                    aria-hidden="true"
                    style={{
                        animation: "coda-overlay-spin 1s linear infinite",
                        filter: "drop-shadow(0 4px 12px rgba(26, 35, 126, 0.25))",
                    }}
                />
                <div
                    style={{
                        fontSize: 12,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: "#1A237E",
                        fontWeight: 600,
                    }}
                >
                    Loading
                </div>
            </div>
            <style>{`
                @keyframes coda-overlay-spin {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(360deg); }
                }
                @keyframes coda-overlay-in {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
            `}</style>
        </div>
    );
}
